import { SingleValue } from "react-select";
import FeatureCard from "../ui/card";
import AssociatiPerformanceChart from "../charts/PerformanceChartCard";
import { useOverview } from "../../context/OverviewContext";
import { PanelCard, PanelHero } from "../ui/panel";
import AppSelect from "../ui/AppSelect";

type LeadType = "linkedin" | "small" | "startup" | "general";

interface OptionType {
  value: LeadType;
  label: string;
}

const options: OptionType[] = [
  { value: "linkedin", label: "Lead LinkedIn" },
  { value: "small", label: "Small Lead" },
  { value: "startup", label: "Startup Toscane" },
  { value: "general", label: "Generale (Tutti)" },
];

export default function OverviewUnified() {
  const { tipo, setTipo, totaleContatti, performance, loadingPerf, conversione, disponibili, topPerformer } =
    useOverview();

  const handleChange = (option: SingleValue<{ value: string; label: string }>) => {
    if (option) setTipo(option.value as LeadType);
  };

  const features = [
    {
      title: "Totale Lead",
      description: totaleContatti !== null ? `${totaleContatti}` : "…",
      accent: "brand" as const,
    },
    {
      title: "Conversione positiva",
      description: conversione,
      descriptionColor: "text-emerald-600",
      accent: "emerald" as const,
    },
    {
      title: "Disponibili in futuro",
      description: disponibili,
      descriptionColor: "text-indigo-600",
      accent: "indigo" as const,
    },
    {
      title: "Top Performer",
      description: topPerformer || "…",
      descriptionColor: "text-amber-600",
      accent: "amber" as const,
    },
  ];

  return (
    <div className="space-y-8 text-left">
      <PanelHero
        eyebrow="Dashboard"
        title="Overview"
        description="Panoramica delle attività e delle performance del team."
        action={
          <div className="w-full min-w-[200px] md:w-[220px]">
            <AppSelect
              options={options}
              value={options.find((o) => o.value === tipo)}
              onChange={handleChange}
              isClearable={false}
              isSearchable={false}
              tone="onDark"
            />
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {features.map((f, idx) => (
          <FeatureCard
            key={idx}
            title={f.title}
            description={f.description}
            descriptionColor={f.descriptionColor}
            accent={f.accent}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <PanelCard title="Performance Team" subtitle="Dettaglio per associato" className="lg:max-h-[520px]">
          {loadingPerf ? (
            <p className="text-secondary/50">Caricamento...</p>
          ) : performance.length === 0 ? (
            <p className="rounded-xl border border-dashed border-secondary/15 py-12 text-center text-sm text-secondary/50">
              Nessun dato performance
            </p>
          ) : (
            <div className="flex max-h-[420px] flex-col gap-3 overflow-y-auto pr-1">
              {performance.map((item, idx) => {
                const name = item.associato || "Non assegnato";
                const initials =
                  name
                    .split(" ")
                    .filter(Boolean)
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase() || "?";
                const percPositivi =
                  item.perc_positivi ??
                  (item.totale_contatti > 0 ? (item.positivi / item.totale_contatti) * 100 : 0);

                return (
                  <div
                    key={idx}
                    className="rounded-xl border border-secondary/10 bg-secondary/[0.02] p-4"
                  >
                    <div className="mb-3 flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-secondary to-[#1e2d4a] text-sm font-bold text-white">
                        {initials}
                      </div>
                      <h4 className="font-extrabold text-secondary">{name}</h4>
                    </div>
                    <div className="mb-2 grid grid-cols-4 gap-2 text-center text-xs">
                      <div>
                        <b className="text-lg text-secondary">{item.totale_contatti}</b>
                        <p className="text-secondary/50">Contatti</p>
                      </div>
                      <div>
                        <b className="text-lg text-emerald-600">{item.positivi}</b>
                        <p className="text-emerald-600/80">Positivi</p>
                      </div>
                      <div>
                        <b className="text-lg text-red-500">{item.negativi}</b>
                        <p className="text-red-500/80">Negativi</p>
                      </div>
                      <div>
                        <b className="text-lg text-amber-500">{item.in_attesa}</b>
                        <p className="text-amber-500/80">In attesa</p>
                      </div>
                    </div>
                    <div className="h-2.5 overflow-hidden rounded-full bg-secondary/10">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 transition-all"
                        style={{ width: `${percPositivi}%` }}
                      />
                    </div>
                    <p className="mt-1 text-right text-[10px] font-semibold text-secondary/60">
                      {percPositivi.toFixed(1)}% conversione
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </PanelCard>

        <PanelCard title="Analisi Performance" subtitle="Distribuzione esiti">
          <div className="min-h-[320px]">
            <AssociatiPerformanceChart tipo={tipo} />
          </div>
        </PanelCard>
      </div>
    </div>
  );
}
